// Solid Geometry (Stereometry) Calculators helpers: volume and surface area
// for the twelve most commonly searched 3D shapes - cube, rectangular prism,
// cylinder, cone, sphere, square pyramid, triangular prism, hemisphere,
// cone frustum, ellipsoid, capsule, and torus.

const PI = Math.PI

// ============================================================
// 1. Cube Calculator (V = a^3, SA = 6a^2, diagonal = a*sqrt(3))
// ============================================================
export function cubeCalculator(params: { side_a: unknown; language: unknown }): {
    volume: number; surface_area: number; diagonal: number; steps: string
} {
    const a = Math.max(Number(params.side_a) || 0, 0)

    const volume = a * a * a
    const surfaceArea = 6 * a * a
    const diagonal = a * Math.sqrt(3)

    const steps = [
        `V = a³ = ${a}³ = ${volume.toFixed(4)}`,
        `SA = 6a² = 6 × ${a}² = ${surfaceArea.toFixed(4)}`,
        `d = a√3 = ${a} × √3 = ${diagonal.toFixed(4)}`,
    ].join('\n')

    return { volume, surface_area: surfaceArea, diagonal, steps }
}

// ============================================================
// 2. Rectangular Prism (Box) Calculator
// ============================================================
export function rectangularPrismCalculator(params: { length: unknown; width: unknown; height: unknown; language: unknown }): {
    volume: number; surface_area: number; diagonal: number; steps: string
} {
    const l = Math.max(Number(params.length) || 0, 0)
    const w = Math.max(Number(params.width) || 0, 0)
    const h = Math.max(Number(params.height) || 0, 0)

    const volume = l * w * h
    const surfaceArea = 2 * (l * w + l * h + w * h)
    const diagonal = Math.sqrt(l * l + w * w + h * h)

    const steps = [
        `V = l × w × h = ${l} × ${w} × ${h} = ${volume.toFixed(4)}`,
        `SA = 2(lw + lh + wh) = 2(${(l * w).toFixed(4)} + ${(l * h).toFixed(4)} + ${(w * h).toFixed(4)}) = ${surfaceArea.toFixed(4)}`,
        `d = √(l² + w² + h²) = √(${l}² + ${w}² + ${h}²) = ${diagonal.toFixed(4)}`,
    ].join('\n')

    return { volume, surface_area: surfaceArea, diagonal, steps }
}

// ============================================================
// 3. Cylinder Calculator (V = pi*r^2*h)
// ============================================================
export function cylinderCalculator(params: { radius: unknown; height: unknown; language: unknown }): {
    volume: number; lateral_surface_area: number; total_surface_area: number; steps: string
} {
    const r = Math.max(Number(params.radius) || 0, 0)
    const h = Math.max(Number(params.height) || 0, 0)

    const volume = PI * r * r * h
    const lateralSurfaceArea = 2 * PI * r * h
    const totalSurfaceArea = 2 * PI * r * (r + h)

    const steps = [
        `V = πr²h = π × ${r}² × ${h} = ${volume.toFixed(4)}`,
        `Lateral SA = 2πrh = 2π × ${r} × ${h} = ${lateralSurfaceArea.toFixed(4)}`,
        `Total SA = 2πr(r + h) = 2π × ${r} × (${r} + ${h}) = ${totalSurfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, lateral_surface_area: lateralSurfaceArea, total_surface_area: totalSurfaceArea, steps }
}

// ============================================================
// 4. Cone Calculator (V = 1/3 pi*r^2*h)
// ============================================================
export function coneCalculator(params: { radius: unknown; height: unknown; language: unknown }): {
    volume: number; slant_height: number; lateral_surface_area: number; total_surface_area: number; steps: string
} {
    const r = Math.max(Number(params.radius) || 0, 0)
    const h = Math.max(Number(params.height) || 0, 0)

    const slantHeight = Math.sqrt(r * r + h * h)
    const volume = (1 / 3) * PI * r * r * h
    const lateralSurfaceArea = PI * r * slantHeight
    const totalSurfaceArea = PI * r * (r + slantHeight)

    const steps = [
        `l = √(r² + h²) = √(${r}² + ${h}²) = ${slantHeight.toFixed(4)}`,
        `V = ⅓πr²h = ⅓ × π × ${r}² × ${h} = ${volume.toFixed(4)}`,
        `Lateral SA = πrl = π × ${r} × ${slantHeight.toFixed(4)} = ${lateralSurfaceArea.toFixed(4)}`,
        `Total SA = πr(r + l) = π × ${r} × (${r} + ${slantHeight.toFixed(4)}) = ${totalSurfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, slant_height: slantHeight, lateral_surface_area: lateralSurfaceArea, total_surface_area: totalSurfaceArea, steps }
}

// ============================================================
// 5. Sphere Calculator (V = 4/3 pi*r^3, SA = 4 pi*r^2)
// ============================================================
export function sphereCalculator(params: { radius: unknown; language: unknown }): {
    volume: number; surface_area: number; diameter: number; steps: string
} {
    const r = Math.max(Number(params.radius) || 0, 0)

    const volume = (4 / 3) * PI * r * r * r
    const surfaceArea = 4 * PI * r * r
    const diameter = 2 * r

    const steps = [
        `V = 4/3πr³ = 4/3 × π × ${r}³ = ${volume.toFixed(4)}`,
        `SA = 4πr² = 4 × π × ${r}² = ${surfaceArea.toFixed(4)}`,
        `d = 2r = 2 × ${r} = ${diameter.toFixed(4)}`,
    ].join('\n')

    return { volume, surface_area: surfaceArea, diameter, steps }
}

// ============================================================
// 6. Square Pyramid Calculator (V = 1/3 * b^2 * h)
// ============================================================
export function squarePyramidCalculator(params: { base_side: unknown; height: unknown; language: unknown }): {
    volume: number; slant_height: number; lateral_surface_area: number; total_surface_area: number; steps: string
} {
    const b = Math.max(Number(params.base_side) || 0, 0)
    const h = Math.max(Number(params.height) || 0, 0)

    const slantHeight = Math.sqrt(h * h + (b / 2) * (b / 2))
    const volume = (1 / 3) * b * b * h
    const lateralSurfaceArea = 2 * b * slantHeight
    const totalSurfaceArea = b * b + lateralSurfaceArea

    const steps = [
        `l = √(h² + (b/2)²) = √(${h}² + (${b}/2)²) = ${slantHeight.toFixed(4)}`,
        `V = ⅓b²h = ⅓ × ${b}² × ${h} = ${volume.toFixed(4)}`,
        `Lateral SA = 2bl = 2 × ${b} × ${slantHeight.toFixed(4)} = ${lateralSurfaceArea.toFixed(4)}`,
        `Total SA = b² + Lateral SA = ${(b * b).toFixed(4)} + ${lateralSurfaceArea.toFixed(4)} = ${totalSurfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, slant_height: slantHeight, lateral_surface_area: lateralSurfaceArea, total_surface_area: totalSurfaceArea, steps }
}

// ============================================================
// 7. Triangular Prism Calculator (right-triangle cross-section: legs base & height)
// ============================================================
export function triangularPrismCalculator(params: { base: unknown; triangle_height: unknown; length: unknown; language: unknown }): {
    volume: number; surface_area: number; steps: string
} {
    const b = Math.max(Number(params.base) || 0, 0)
    const th = Math.max(Number(params.triangle_height) || 0, 0)
    const len = Math.max(Number(params.length) || 0, 0)

    const hypotenuse = Math.sqrt(b * b + th * th)
    const crossSectionArea = 0.5 * b * th
    const volume = crossSectionArea * len
    const perimeter = b + th + hypotenuse
    const surfaceArea = 2 * crossSectionArea + perimeter * len

    const steps = [
        `Cross-section area = ½ × base × height = ½ × ${b} × ${th} = ${crossSectionArea.toFixed(4)}`,
        `V = Area × length = ${crossSectionArea.toFixed(4)} × ${len} = ${volume.toFixed(4)}`,
        `Hypotenuse = √(base² + height²) = √(${b}² + ${th}²) = ${hypotenuse.toFixed(4)}`,
        `SA = 2 × Area + Perimeter × length = 2 × ${crossSectionArea.toFixed(4)} + ${perimeter.toFixed(4)} × ${len} = ${surfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, surface_area: surfaceArea, steps }
}

// ============================================================
// 8. Hemisphere Calculator (V = 2/3 pi*r^3)
// ============================================================
export function hemisphereCalculator(params: { radius: unknown; language: unknown }): {
    volume: number; curved_surface_area: number; total_surface_area: number; steps: string
} {
    const r = Math.max(Number(params.radius) || 0, 0)

    const volume = (2 / 3) * PI * r * r * r
    const curvedSurfaceArea = 2 * PI * r * r
    const totalSurfaceArea = 3 * PI * r * r

    const steps = [
        `V = 2/3πr³ = 2/3 × π × ${r}³ = ${volume.toFixed(4)}`,
        `Curved SA = 2πr² = 2 × π × ${r}² = ${curvedSurfaceArea.toFixed(4)}`,
        `Total SA = 3πr² = 3 × π × ${r}² = ${totalSurfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, curved_surface_area: curvedSurfaceArea, total_surface_area: totalSurfaceArea, steps }
}

// ============================================================
// 9. Cone Frustum (Truncated Cone) Calculator
// ============================================================
export function coneFrustumCalculator(params: { top_radius: unknown; bottom_radius: unknown; height: unknown; language: unknown }): {
    volume: number; slant_height: number; lateral_surface_area: number; total_surface_area: number; steps: string
} {
    const r1 = Math.max(Number(params.top_radius) || 0, 0)
    const r2 = Math.max(Number(params.bottom_radius) || 0, 0)
    const h = Math.max(Number(params.height) || 0, 0)

    const slantHeight = Math.sqrt(h * h + (r2 - r1) * (r2 - r1))
    const volume = (1 / 3) * PI * h * (r1 * r1 + r2 * r2 + r1 * r2)
    const lateralSurfaceArea = PI * (r1 + r2) * slantHeight
    const totalSurfaceArea = lateralSurfaceArea + PI * r1 * r1 + PI * r2 * r2

    const steps = [
        `l = √(h² + (r₂ - r₁)²) = √(${h}² + (${r2} - ${r1})²) = ${slantHeight.toFixed(4)}`,
        `V = ⅓πh(r₁² + r₂² + r₁r₂) = ⅓ × π × ${h} × (${r1}² + ${r2}² + ${r1}×${r2}) = ${volume.toFixed(4)}`,
        `Lateral SA = π(r₁ + r₂)l = π × (${r1} + ${r2}) × ${slantHeight.toFixed(4)} = ${lateralSurfaceArea.toFixed(4)}`,
        `Total SA = Lateral SA + πr₁² + πr₂² = ${lateralSurfaceArea.toFixed(4)} + ${(PI * r1 * r1).toFixed(4)} + ${(PI * r2 * r2).toFixed(4)} = ${totalSurfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, slant_height: slantHeight, lateral_surface_area: lateralSurfaceArea, total_surface_area: totalSurfaceArea, steps }
}

// ============================================================
// 10. Ellipsoid Calculator (V exact, SA via Thomsen approximation, p ~= 1.6075)
// ============================================================
const THOMSEN_P = 1.6075

export function ellipsoidCalculator(params: { semi_axis_a: unknown; semi_axis_b: unknown; semi_axis_c: unknown; language: unknown }): {
    volume: number; surface_area: number; steps: string
} {
    const a = Math.max(Number(params.semi_axis_a) || 0, 0)
    const b = Math.max(Number(params.semi_axis_b) || 0, 0)
    const c = Math.max(Number(params.semi_axis_c) || 0, 0)

    const volume = (4 / 3) * PI * a * b * c
    const ap = Math.pow(a, THOMSEN_P)
    const bp = Math.pow(b, THOMSEN_P)
    const cp = Math.pow(c, THOMSEN_P)
    const surfaceArea = 4 * PI * Math.pow((ap * bp + ap * cp + bp * cp) / 3, 1 / THOMSEN_P)

    const steps = [
        `V = 4/3πabc = 4/3 × π × ${a} × ${b} × ${c} = ${volume.toFixed(4)}`,
        `SA ≈ 4π × ((a^p×b^p + a^p×c^p + b^p×c^p) / 3)^(1/p), p ≈ 1.6075 (Thomsen approximation)`,
        `SA ≈ ${surfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, surface_area: surfaceArea, steps }
}

// ============================================================
// 11. Capsule Calculator (cylinder with two hemispherical caps)
// ============================================================
export function capsuleCalculator(params: { radius: unknown; cylinder_height: unknown; language: unknown }): {
    volume: number; surface_area: number; total_length: number; steps: string
} {
    const r = Math.max(Number(params.radius) || 0, 0)
    const h = Math.max(Number(params.cylinder_height) || 0, 0)

    const cylinderVolume = PI * r * r * h
    const sphereVolume = (4 / 3) * PI * r * r * r
    const volume = cylinderVolume + sphereVolume
    const surfaceArea = 2 * PI * r * h + 4 * PI * r * r
    const totalLength = h + 2 * r

    const steps = [
        `V_cylinder = πr²h = π × ${r}² × ${h} = ${cylinderVolume.toFixed(4)}`,
        `V_sphere = 4/3πr³ = 4/3 × π × ${r}³ = ${sphereVolume.toFixed(4)}`,
        `V = V_cylinder + V_sphere = ${cylinderVolume.toFixed(4)} + ${sphereVolume.toFixed(4)} = ${volume.toFixed(4)}`,
        `SA = 2πrh + 4πr² = ${(2 * PI * r * h).toFixed(4)} + ${(4 * PI * r * r).toFixed(4)} = ${surfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, surface_area: surfaceArea, total_length: totalLength, steps }
}

// ============================================================
// 12. Torus Calculator (V = 2 pi^2 R r^2, SA = 4 pi^2 R r)
// ============================================================
export function torusCalculator(params: { tube_radius: unknown; center_radius: unknown; language: unknown }): {
    volume: number; surface_area: number; steps: string
} {
    const r = Math.max(Number(params.tube_radius) || 0, 0)
    const R = Math.max(Number(params.center_radius) || 0, 0)

    const volume = 2 * PI * PI * R * r * r
    const surfaceArea = 4 * PI * PI * R * r

    const steps = [
        `V = 2π²Rr² = 2 × π² × ${R} × ${r}² = ${volume.toFixed(4)}`,
        `SA = 4π²Rr = 4 × π² × ${R} × ${r} = ${surfaceArea.toFixed(4)}`,
    ].join('\n')

    return { volume, surface_area: surfaceArea, steps }
}
